import React, { useEffect, useRef, useState } from 'react';
import { Check, Package, RotateCcw, Warehouse } from 'lucide-react';
import './ShippingStory.css';

export const SHIPPING_DEMO_MS = 6600;
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;
const STAGES = [
  { title: 'Inbound restock', detail: 'Supply moving to the US warehouse', event: 'Example carrier scan', Icon: Package },
  { title: 'Warehouse receipt', detail: 'The team confirms physical receipt', event: 'Example warehouse confirmation', Icon: Warehouse },
  { title: 'Your delivery', detail: 'A separate parcel travels to you', event: 'Example customer delivery', Icon: Check },
];

/** A finite educational illustration. No orders, live feeds, or carrier claims. */
export default function ShippingStory() {
  const ref = useRef(null);
  const [reduced, setReduced] = useState(reducedMotion);
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(() => reducedMotion() ? SHIPPING_DEMO_MS : 0);
  const complete = elapsed >= SHIPPING_DEMO_MS;
  const completed = Math.min(3, Math.floor(elapsed / 2200));
  const running = visible && !hidden && !paused && !reduced && !complete;

  useEffect(() => {
    if (!window.IntersectionObserver) { setVisible(true); return undefined; }
    const observer = new window.IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const change = event => { setReduced(event.matches); if (event.matches) setElapsed(SHIPPING_DEMO_MS); };
    const visibility = () => setHidden(document.hidden);
    query?.addEventListener?.('change', change);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      query?.removeEventListener?.('change', change);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setElapsed(value => Math.min(SHIPPING_DEMO_MS, value + 100)), 100);
    return () => window.clearInterval(timer);
  }, [running]);

  return (
    <div ref={ref} className="shipping-story" data-testid="shipping-story" data-running={running}>
      <div className="shipping-story__header">
        <div>
          <p className="shipping-story__label">An order journey / illustrated</p>
          <p className="shipping-story__disclosure">Demonstration only. Not a live shipment.</p>
        </div>
        <div className="shipping-story__controls">
          {!complete && !reduced && <button type="button" onClick={() => setPaused(value => !value)} aria-label={paused ? 'Resume shipping demonstration' : 'Pause shipping demonstration'}>{paused ? 'Resume' : 'Pause'}</button>}
          {!reduced && <button type="button" onClick={() => { setElapsed(0); setPaused(false); }} aria-label="Replay the journey"><RotateCcw size={13} aria-hidden="true" />Replay the journey</button>}
        </div>
      </div>
      <div className="shipping-story__legs" aria-hidden="true"><span>01 / Supply to warehouse</span><span>02 / Warehouse to you</span></div>
      <ol className="shipping-story__rail" aria-label="Illustrated replenishment and delivery stages">
        {STAGES.map(({ title, detail, event, Icon }, index) => (
          <li key={title} className="shipping-story__stage" data-step={index} data-complete={completed > index} data-active={!complete && completed === index}>
            {(index === 0 || index === 2) && <span className="shipping-story__mobile-leg" aria-hidden="true">{index === 0 ? 'Supply to warehouse' : 'Warehouse to you'}</span>}
            <div className="shipping-story__track" aria-hidden="true">
              <span className="shipping-story__node"><Icon size={21} strokeWidth={1.4} /></span>
              <span className="shipping-story__connector"><i style={{ '--progress': Math.max(0, Math.min(1, (elapsed - index * 2200) / 2200)) }} /></span>
            </div>
            <div className="shipping-story__stage-copy"><span className="shipping-story__number">0{index + 1}</span><h3>{title}</h3><p>{detail}</p><span className="shipping-story__event">{completed > index ? <Check size={12} aria-hidden="true" /> : <span aria-hidden="true">·</span>}{event}</span></div>
          </li>
        ))}
      </ol>
      <div className="shipping-story__footer">
        <p>Restocking our warehouse and delivering your order are separate trips. <strong>A warehouse arrival is not your doorstep.</strong></p>
        <p className="shipping-story__status" role="status">{complete ? 'Illustration complete' : paused ? 'Illustration paused' : `Illustrating: ${STAGES[completed].title.toLowerCase()}`}</p>
      </div>
    </div>
  );
}
